import { Form, Input, Space } from 'antd';
import React, { Key, useCallback, useMemo, useState } from 'react';
import Filter from '@/shared/components/Filter';
import { Paging, Table, usePaging } from '@maxtropy/components';
import { useQuery } from '../../../utils/utils';
import { EdgeTemplateTenantRequest, EdgeTemplateTenantRes, getEdgeTemplateTenant } from '../../../api/edgeTemplate';
import { selectTenantColumns } from '../utils';

interface TenantModalProps {
  value?: EdgeTemplateTenantRes[];
  onChange?: (value: EdgeTemplateTenantRes[]) => void;
  disabledIds?: Key[];
}

const TenantModal: React.FC<TenantModalProps> = ({ value, onChange, disabledIds }) => {
  const [form] = Form.useForm();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<EdgeTemplateTenantRequest>();
  const [tenants, setTenants] = useState<EdgeTemplateTenantRes[]>([]);

  const { data, isLoading } = useQuery(
    useCallback(
      () =>
        getEdgeTemplateTenant({
          ...searchParams,
          page: pageOffset,
          size: pageSize,
        }).then(res => {
          if (res) {
            setTotalCount(res.total);
            setTenants(res.list);
          }
          return res.list;
        }),
      [pageOffset, pageSize, searchParams, setTotalCount]
    )
  );

  const tenantIds = useMemo(() => {
    return (value ?? []).map(i => i.rootMcid);
  }, [value]);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: EdgeTemplateTenantRes[]) => {
      const tenantsId = tenants.map(i => i.rootMcid);
      const buildDataMap = new Map<string, EdgeTemplateTenantRes>();
      (value ?? [])
        .filter(i => !tenantsId.includes(i.rootMcid))
        .forEach(i => {
          if (!buildDataMap.has(i.rootMcid)) {
            buildDataMap.set(i.rootMcid, i);
          }
        });
      selectedRows.forEach(i => {
        if (!buildDataMap.has(i.rootMcid)) {
          buildDataMap.set(i.rootMcid, i);
        }
      });
      onChange && onChange(Array.from(buildDataMap.values()));
    },
    getCheckboxProps: (record: EdgeTemplateTenantRes) => ({
      disabled: (disabledIds ?? []).includes(record.rootMcid),
    }),
    selectedRowKeys: tenantIds,
  };

  const onReset = () => {
    const params = {
      ...searchParams,
      name: undefined,
      tenantCode: undefined,
    };
    setSearchParams(params as EdgeTemplateTenantRequest);
    setPageOffset(1);
  };

  const onFinish = (val: EdgeTemplateTenantRequest) => {
    setSearchParams({ ...val });
    setPageOffset(1);
  };

  const filters = (
    <Filter form={form} onFinish={val => onFinish(val as EdgeTemplateTenantRequest)} onReset={onReset}>
      <Space size={15}>
        <Form.Item label="租户名称" name="name">
          <Input placeholder="请输入租户名称查询" />
        </Form.Item>
        <Form.Item label="租户code" name="tenantCode">
          <Input placeholder="请输入租户code查询" />
        </Form.Item>
      </Space>
    </Filter>
  );

  return (
    <>
      {filters}
      <Table
        loading={isLoading}
        sticky
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        rowKey="rootMcid"
        scroll={{ y: 300 }}
        columns={selectTenantColumns}
        dataSource={data}
      />
      <Paging pagingInfo={pagingInfo} />
    </>
  );
};

export default TenantModal;
